import { SupportedLocaleScript } from "@myvp/shared/src/i18n/get-locale-script"

const primitiveTheme = {
  neutral: {
    100: "#FFFFFF",
    200: "#FBFCFF",
    300: "#F2F6FC",
    400: "#F0F4FB",
    500: "#EBEFF6",
    600: "#E9ECF4",
    700: "#E3E7EE",
    800: "#D8DCE3",
    900: "#C3C8D1",
    1000: "#AAAFB8",
    1100: "#6A6E75",
    1200: "#5E6269",
    1300: "#52565C",
    1400: "#464950",
    1500: "#3A3D43",
    1600: "#2F3137",
    1700: "#22252A",
    1800: "#16181E",
    1900: "#0B0D13",
    2000: "#000000",
  },
  orange: {
    100: "#FFFBF7",
    200: "#FFF8EE",
    300: "#FFF2E6",
    400: "#FFECD8",
    500: "#FFE4C2",
    600: "#FDD7A9",
    700: "#FBC27B",
    800: "#F9AD4C",
    900: "#F7981D",
    1000: "#ED8600",
    1100: "#CD7300",
    1200: "#AD6200",
    1300: "#AB5200",
    1400: "#874C00",
    1500: "#724100",
    1600: "#7E3C00",
    1700: "#4A2C11",
    1800: "#3A2000",
    1900: "#271600",
    2000: "#140B00",
  },
  blue: {
    100: "#F0F7FF",
    200: "#DAEBFF",
    300: "#C3E0FE",
    400: "#ADD4FE",
    500: "#97C8FD",
    600: "#81BDFD",
    700: "#6AB1FC",
    800: "#54A5FC",
    900: "#3E99FB",
    1000: "#178EFB",
    1100: "#1182FA",
    1200: "#0F75E1",
    1300: "#0D67C8",
    1400: "#0B5AAF",
    1500: "#094D96",
    1600: "#083F7E",
    1700: "#063265",
    1800: "#04254C",
    1900: "#021733",
    2000: "#000A1A",
  },
  green: {
    100: "#F7FEF9",
    200: "#E1FBE6",
    300: "#CDF8D1",
    400: "#BAECC0",
    500: "#A6E0AE",
    600: "#92D49E",
    700: "#7EC88D",
    800: "#68BC7D",
    900: "#51B06D",
    1000: "#36A55E",
    1100: "#04994E",
    1200: "#008947",
    1300: "#007A40",
    1400: "#006B38",
    1500: "#005D31",
    1600: "#004E29",
    1700: "#004122",
    1800: "#00331A",
    1900: "#002613",
    2000: "#001A0C",
  },
  yellow: {
    100: "#FFFBF0",
    200: "#FFF9DC",
    300: "#FEF1CF",
    400: "#FFF4B9",
    500: "#FEE6AC",
    600: "#FFE387",
    700: "#FEDA87",
    800: "#FED473",
    900: "#F5D158",
    1000: "#EBC429",
    1100: "#FFC107",
    1200: "#E4AF0A",
    1300: "#C59603",
    1400: "#C38D00",
    1500: "#8F6D01",
    1600: "#85620B",
    1700: "#674A00",
    1800: "#453400",
    1900: "#2F2200",
    2000: "#1A1200",
  },
  red: {
    100: "#FFFBFB",
    200: "#FFF6F6",
    300: "#FFECEC",
    400: "#FFE7E7",
    500: "#FFE1E2",
    600: "#FFDCDC",
    700: "#FFCBCB",
    800: "#FFB3B3",
    900: "#FF9393",
    1000: "#FF7171",
    1100: "#FF4C4C",
    1200: "#F83E3E",
    1300: "#E42A2A",
    1400: "#D81E1E",
    1500: "#CF000F",
    1600: "#BF0505",
    1700: "#940000",
    1800: "#7A0000",
    1900: "#520000",
    2000: "#2A0000",
  },
  purple: {
    100: "#FEFBFF",
    200: "#FCF6FF",
    300: "#FAF1FF",
    400: "#F2DEFE",
    500: "#E9CBFD",
    600: "#DFB9FC",
    700: "#D5A6FB",
    800: "#CA93FA",
    900: "#BF80F9",
    1000: "#B46BF8",
    1100: "#A855F7",
    1200: "#9947EA",
    1300: "#8A38DC",
    1400: "#7B27CF",
    1500: "#6D11C2",
    1600: "#5B0BA3",
    1700: "#4A0685",
    1800: "#3A0368",
    1900: "#29014D",
    2000: "#1A0033",
  },
  slate: {
    100: "#FAFCFF",
    200: "#F6F9FF",
    300: "#EEF4FF",
    400: "#E6F0FF",
    500: "#E3EEFF",
    600: "#DAE8FF",
    700: "#CBDEFC",
    800: "#B7CAE8",
    900: "#9EB1CF",
    1000: "#90A3C1",
    1100: "#7184A2",
    1200: "#5D708E",
    1300: "#516482",
    1400: "#455876",
    1500: "#384B69",
    1600: "#2C3F5D",
    1700: "#203351",
    1800: "#142745",
    1900: "#061937",
    2000: "#000E25",
  },
} as const

export const theme = {
  isRTL: false as boolean,
  localeScript: "Latn" as SupportedLocaleScript,
  primitive: { ...primitiveTheme },
  palette: {
    content: {
      neutral: {
        inverse: primitiveTheme.neutral[200],
        default: primitiveTheme.neutral[1600],
        subtle: primitiveTheme.neutral[1100],
        disabled: primitiveTheme.neutral[1000],
        readonly: primitiveTheme.neutral[1500],
        readonlysubtle: primitiveTheme.neutral[1000],
      },
      primary: {
        default: primitiveTheme.blue[1300],
        strong: primitiveTheme.blue[1600],
        hover: primitiveTheme.blue[1000],
        pressed: primitiveTheme.blue[1400],
        disabled: primitiveTheme.blue[600],
      },
      secondary: {
        default: primitiveTheme.orange[900],
        strong: primitiveTheme.orange[1600],
        hover: primitiveTheme.orange[800],
        pressed: primitiveTheme.orange[1000],
        disabled: primitiveTheme.orange[600],
      },
      link: {
        default: primitiveTheme.blue[1300],
        visited: primitiveTheme.blue[1300],
        hover: primitiveTheme.blue[1000],
        pressed: primitiveTheme.blue[1400],
        disabled: primitiveTheme.neutral[1000],
      },
      success: {
        default: primitiveTheme.green[1200],
        strong: primitiveTheme.green[1700],
        hover: primitiveTheme.green[900],
        pressed: primitiveTheme.green[1400],
      },
      warning: {
        default: primitiveTheme.yellow[1400],
        strong: primitiveTheme.yellow[1700],
        hover: primitiveTheme.yellow[1100],
        pressed: primitiveTheme.yellow[1500],
      },
      critical: {
        default: primitiveTheme.red[1500],
        strong: primitiveTheme.red[1700],
        hover: primitiveTheme.red[1300],
        pressed: primitiveTheme.red[1700],
      },
      info: {
        default: primitiveTheme.blue[1300],
        strong: primitiveTheme.blue[1400],
        hover: primitiveTheme.blue[1000],
        pressed: primitiveTheme.blue[1500],
      },
      infoAlt: {
        default: primitiveTheme.purple[1300],
        strong: primitiveTheme.purple[1700],
        hover: primitiveTheme.purple[1100],
        pressed: primitiveTheme.purple[1400],
      },
    },
    surface: {
      neutral: {
        default: primitiveTheme.neutral[100],
        alt: primitiveTheme.neutral[300],
        strong: primitiveTheme.neutral[800],
        hover: primitiveTheme.neutral[200],
        pressed: primitiveTheme.neutral[400],
        disabled: primitiveTheme.neutral[300],
        readonly: primitiveTheme.neutral[400],
        readonlyalt: primitiveTheme.neutral[800],
        selected: primitiveTheme.neutral[600],
        selectedhover: primitiveTheme.neutral[400],
        selectedpress: primitiveTheme.neutral[500],
        selecteddisabled: primitiveTheme.neutral[600],
      },
      primary: {
        default: primitiveTheme.blue[200],
        strong: primitiveTheme.blue[1400],
        hover: primitiveTheme.blue[1100],
        pressed: primitiveTheme.blue[1500],
        disabled: primitiveTheme.blue[400],
        selectedhover: primitiveTheme.blue[1200],
      },
      secondary: {
        default: primitiveTheme.orange[400],
        strong: primitiveTheme.orange[900],
        hover: primitiveTheme.orange[800],
        pressed: primitiveTheme.orange[1000],
        disabled: primitiveTheme.orange[600],
        selected: primitiveTheme.orange[400],
        selectedhover: primitiveTheme.orange[600],
        selectedpress: primitiveTheme.orange[700],
      },
      success: {
        default: primitiveTheme.green[300],
        strong: primitiveTheme.green[400],
        hover: primitiveTheme.green[400],
        pressed: primitiveTheme.green[600],
        disabled: primitiveTheme.green[200],
      },
      warning: {
        default: primitiveTheme.yellow[200],
        strong: primitiveTheme.yellow[500],
        hover: primitiveTheme.yellow[400],
        pressed: primitiveTheme.yellow[700],
        disabled: primitiveTheme.yellow[200],
      },
      critical: {
        default: primitiveTheme.red[400],
        strong: primitiveTheme.red[700],
        hover: primitiveTheme.red[800],
        pressed: primitiveTheme.red[900],
        disabled: primitiveTheme.red[400],
      },
      info: {
        default: primitiveTheme.blue[200],
        strong: primitiveTheme.blue[400],
        hover: primitiveTheme.blue[300],
        pressed: primitiveTheme.blue[400],
        disabled: primitiveTheme.blue[200],
      },
      infoAlt: {
        default: primitiveTheme.purple[400],
        strong: primitiveTheme.purple[500],
        hover: primitiveTheme.purple[600],
        pressed: primitiveTheme.purple[700],
        disabled: primitiveTheme.purple[300],
      },
      vault: {
        default: primitiveTheme.slate[1900],
        hover: primitiveTheme.slate[1400],
        pressed: primitiveTheme.slate[1800],
        selected: primitiveTheme.slate[1600],
      },
      tooltip: {
        default: primitiveTheme.neutral[1600],
        hover: primitiveTheme.neutral[1400],
        pressed: primitiveTheme.neutral[1200],
      },
    },
    border: {
      neutral: {
        default: primitiveTheme.neutral[800],
        subtle: primitiveTheme.neutral[500],
      },
      focus: {
        default: primitiveTheme.blue[1300],
      },
      secondary: {
        default: primitiveTheme.orange[900],
      },
      success: {
        default: primitiveTheme.green[1000],
      },
      warning: {
        default: primitiveTheme.yellow[1200],
      },
      critical: {
        default: primitiveTheme.red[1500],
      },
    },
    background: {
      neutral: {
        default: primitiveTheme.neutral[500],
        alt: primitiveTheme.neutral[100],
        overlay: "rgba(39, 42, 48, 0.6)",
      },
    },
    data: {
      neutral: {
        "level-1": primitiveTheme.orange[1100],
        "level-2": primitiveTheme.orange[1000],
        "level-3": primitiveTheme.orange[800],
        "level-4": primitiveTheme.orange[600],
        "level-5": primitiveTheme.orange[400],
        "level-6": primitiveTheme.orange[1200],
        "level-7": primitiveTheme.orange[1400],
        "level-8": primitiveTheme.orange[1600],
      },
      success: {
        "level-1": primitiveTheme.green[1100],
        "level-2": primitiveTheme.green[900],
        "level-3": primitiveTheme.green[700],
        "level-4": primitiveTheme.green[500],
        "level-5": primitiveTheme.green[300],
        "level-6": primitiveTheme.green[1300],
        "level-7": primitiveTheme.green[1500],
        "level-8": primitiveTheme.green[1700],
      },
      critical: {
        "level-1": primitiveTheme.red[1200],
        "level-2": primitiveTheme.red[1000],
        "level-3": primitiveTheme.red[800],
        "level-4": primitiveTheme.red[600],
        "level-5": primitiveTheme.red[400],
        "level-6": primitiveTheme.red[1400],
        "level-7": primitiveTheme.red[1600],
        "level-8": primitiveTheme.red[1800],
      },
    },
  },
  zIndex: {
    /**
     * Below deprecated modals + backdrops.
     */
    tableHeader: 997,
    alertlist: 998,
    modalBackdrop: 999,
    modal: 1000,
    drawer: 1002,
    select: 1003,
    tooltip: 1004,
  },
  /** @deprecated */
  addFocus: `:focus {
    box-shadow: 0 0 4pt 2pt rgba(242, 153, 74, 0.6);
    border-radius: 8px;
    z-index: 1;
  }`,
  /** @deprecated */
  font: {
    family: "Inter, sans-serif",
  },
} as const

export type Theme = typeof theme
